import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton, Chip } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useLazyListMerchantsQuery } from "store/services/merchantApi";

import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";

import { useAppMeta, useTablePagination } from "hooks";

const getStatusStyle = (status) => {
  const styles = {
    Active: { backgroundColor: "#EBF5FB", textColor: "green" },
    Inactive: { backgroundColor: "#FDEDEC", textColor: "#E74C3C" },
  };

  return styles[status] || {
    backgroundColor: "#F8F9F9",
    textColor: "#7F8C8D",
  };
};

const MerchantListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Merchants" }];

  const merchantTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Merchant Name",
      selector: (row) => row.merchantName,
      cell: (row) => (
        <Link
          title={row?.merchantName}
          to={`/app/crm/merchant/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.merchantName ? (
            <HighlightedText text={row?.merchantName} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Merchant Code",
      selector: (row) => row.merchantCode,
      cell: (row) => row.merchantCode || "N/A",
    },
    {
      name: "Location",
      selector: (row) => row.location,
      cell: (row) => row.location || "N/A",
    },
    {
      name: "Merchant Type",
      selector: (row) => row.merchantType,
      cell: (row) => row.merchantType || "N/A",
    },
    {
      name: "Added By",
      selector: (row) => row.addedBy,
      cell: (row) => row.addedBy.name || "N/A",
    },
    {
      name: "Status",
      selector: (row) => row.active,
      cell: (row) => {
        const statusName = row.active ? "Active" : "Inactive";
        const { backgroundColor, textColor } = getStatusStyle(statusName);

        return (
          <Chip
            label={statusName}
            size="small"
            sx={{
              backgroundColor,
              color: textColor,
              fontWeight: 500,
              textTransform: "capitalize",
            }}
          />
        );
      },
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/merchant/${row._id}/detail`}>
            <IconButton size="small" aria-label="View Merchant" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/merchant/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit Merchant">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchMerchants,
    { data: merchantRes, isLoading, isFetching, error },
  ] = useLazyListMerchantsQuery();

  React.useEffect(() => {
    fetchMerchants({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchMerchants, keyword, perPage]);

  useAppMeta(`${activeTab} Merchants`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={merchantTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Search Merchants"
          disabled={isLoading || isFetching}
        ></TableFilter>

        <AppTable
          columns={columns}
          data={merchantRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={merchantRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default MerchantListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/merchant/create">
      <Button startIcon={<Add />} variant="contained">
        New Merchant
      </Button>
    </Link>
  );
};